import React from 'react';
import { Shield, Code, AlertCircle, Lock } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';

export function ApplicationSecurity() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Sécurité des Applications',
        en: 'Application Security'
      },
      subtitle: {
        fr: 'Protection avancée de vos applications web et mobiles',
        en: 'Advanced protection for your web and mobile applications'
      }
    },
    features: [
      {
        icon: <Code className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Analyse du Code',
          en: 'Code Analysis'
        },
        description: {
          fr: 'Détection des vulnérabilités dans le code source',
          en: 'Detection of vulnerabilities in source code'
        }
      },
      {
        icon: <Shield className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Protection Runtime',
          en: 'Runtime Protection'
        },
        description: {
          fr: 'Sécurité en temps réel pendant l\'exécution',
          en: 'Real-time security during execution'
        }
      },
      {
        icon: <Lock className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Sécurité API',
          en: 'API Security'
        },
        description: {
          fr: 'Protection complète des API et des services web',
          en: 'Complete protection of APIs and web services'
        }
      }
    ],
    capabilities: {
      title: {
        fr: 'Fonctionnalités de Sécurité',
        en: 'Security Features'
      },
      items: [
        {
          title: {
            fr: 'Tests de Sécurité',
            en: 'Security Testing'
          },
          description: {
            fr: 'Tests automatisés pour identifier les vulnérabilités',
            en: 'Automated testing to identify vulnerabilities'
          }
        },
        {
          title: {
            fr: 'WAF Intelligent',
            en: 'Smart WAF'
          },
          description: {
            fr: 'Pare-feu applicatif avec apprentissage automatique',
            en: 'Application firewall with machine learning'
          }
        },
        {
          title: {
            fr: 'Monitoring Continu',
            en: 'Continuous Monitoring'
          },
          description: {
            fr: 'Surveillance en temps réel des menaces applicatives',
            en: 'Real-time monitoring of application threats'
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Code className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Application Security Dashboard */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&q=80" 
              alt="Application Monitoring"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Surveillance des Applications' : 'Application Monitoring'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Détection en temps réel des vulnérabilités applicatives'
                  : 'Real-time detection of application vulnerabilities'}
              </p>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80" 
              alt="Security Testing"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Tests de Sécurité' : 'Security Testing'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Analyse continue du code et des composants'
                  : 'Continuous code and component analysis'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Security View */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
          <img 
            src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80" 
            alt="Security Operations Center"
            className="w-full h-96 object-cover"
          />
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-2">
              {language === 'fr' ? 'Centre des Opérations' : 'Operations Center'}
            </h3>
            <p className="text-gray-300">
              {language === 'fr' 
                ? 'Surveillance 24/7 de la sécurité de vos applications'
                : '24/7 monitoring of your application security'}
            </p>
          </div>
        </div>
      </div>

      {/* Capabilities Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.capabilities.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {content.capabilities.items.map((item, index) => (
              <div key={index} className="flex items-start space-x-4 bg-gray-900/50 p-6 rounded-lg">
                <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {item.title[language]}
                  </h3>
                  <p className="text-gray-300">
                    {item.description[language]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Sécurisez Vos Applications' : 'Secure Your Applications'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Protégez vos applications avec notre solution de sécurité avancée' 
                : 'Protect your applications with our advanced security solution'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Commencer Maintenant' : 'Get Started Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}